import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Download,
  RotateCcw,
  Plus,
  Trash2,
  ExternalLink,
  Upload,
  Image as ImageIcon,
  X,
  AlertTriangle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";

import chirpwellLogo from "@/assets/chirpwell-logo.png";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import {
  PALETTES,
  SOCIAL_SIZES,
  TEMPLATES,
  EMPTY_CONTENT,
  getDailySources,
  todayKey,
  FONT_OPTIONS,
  FONT_SLOT_LABELS,
  DEFAULT_FONTS,
  type FontId,
  type FontSlot,
  DECOR_OPTIONS,
  DECOR_PLACEMENTS,
  type DecorPlacement,
  FONT_SCALE_MIN,
  FONT_SCALE_MAX,
  type DecorId,
  IMAGE_FITS,
  type ImageFit,
  customPalette,
  isValidHex,
  renderTemplate,
  type TemplateContent,
  type TemplateDef,
  type TemplateTag,
  TEMPLATE_TAGS,
} from "@/lib/socialTemplates";

const TAG_FILTERS: { id: TemplateTag | "all"; label: string }[] = [
  { id: "all", label: "All templates" },
  ...TEMPLATE_TAGS,
];

const TAG_LABEL: Record<TemplateTag, string> = TEMPLATE_TAGS.reduce(
  (acc, t) => ({ ...acc, [t.id]: t.label }),
  {} as Record<TemplateTag, string>
);

const inputClass =
  "w-full rounded-lg bg-brand-surface border border-brand-line px-3 py-2 text-sm text-brand-ink placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-ink transition-colors";

const sectionClass = "rounded-2xl p-5 border space-y-4";
const sectionStyle = {
  background: "hsl(var(--brand-surface))",
  borderColor: "hsl(var(--brand-line))",
} as const;

const labelClass = "text-xs font-semibold tracking-widest text-brand-muted";

interface UploadedImage {
  id: string;
  name: string;
  src: string;
}

const SocialTemplates = () => {
  const [tagFilter, setTagFilter] = useState<TemplateTag | "all">("all");
  const [templateId, setTemplateId] = useState<TemplateDef["id"]>(TEMPLATES[0].id);
  const [sizeId, setSizeId] = useState(SOCIAL_SIZES[0].id);
  const [paletteId, setPaletteId] = useState(PALETTES[0].id);
  const [custom, setCustom] = useState({
    bg: "#12121A",
    bgAlt: "#1E1B2E",
    accent: "#F97316",
    text: "#FFFFFF",
  });
  const [content, setContent] = useState<TemplateContent>({
    ...EMPTY_CONTENT,
    ...TEMPLATES[0].defaults,
  } as TemplateContent);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const [logoReady, setLogoReady] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string>(chirpwellLogo);
  const [customLogoName, setCustomLogoName] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // background image library
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const [imageFit, setImageFit] = useState<ImageFit>("cover");
  const [bgScrim, setBgScrim] = useState(0.45);
  const [bgOffsetX, setBgOffsetX] = useState(0);
  const [bgOffsetY, setBgOffsetY] = useState(0);
  const [bgZoom, setBgZoom] = useState(1);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  const [bgReady, setBgReady] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // text color
  const [autoContrast, setAutoContrast] = useState(true);
  const [textColor, setTextColor] = useState("");

  const [fonts, setFonts] = useState<Partial<Record<FontSlot, FontId>>>({});
  const [fontScales, setFontScales] = useState<Partial<Record<FontSlot, number>>>({});
  const [decor, setDecor] = useState<DecorId>("none");
  const [decorOpacity, setDecorOpacity] = useState(0.18);
  const [decorColor, setDecorColor] = useState("");
  const [decorScale, setDecorScale] = useState(1);
  const [decorRotation, setDecorRotation] = useState(0);
  const [decorPlacement, setDecorPlacement] = useState<DecorPlacement>("default");

  /* marketing-compliance acknowledgement gate (once per browser session) */
  const [ackOpen, setAckOpen] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [ackDone, setAckDone] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("cw-marketing-ack") === "1"
  );



  const template = useMemo(
    () => TEMPLATES.find((t) => t.id === templateId)!,
    [templateId]
  );
  const size = useMemo(() => SOCIAL_SIZES.find((s) => s.id === sizeId)!, [sizeId]);
  // Rotates automatically at UTC midnight so the featured links change daily.
  const dailySources = useMemo(() => getDailySources(6), []);

  const palette = useMemo(() => {
    if (paletteId === "custom") {
      const safe = (v: string, fb: string) => (isValidHex(v) ? v : fb);
      return customPalette(
        safe(custom.bg, "#0A1628"),
        safe(custom.bgAlt, custom.bg),
        safe(custom.accent, "#FBBF24"),
        safe(custom.text, "#FFFFFF")
      );
    }
    return PALETTES.find((p) => p.id === paletteId)!;
  }, [paletteId, custom]);

  const visibleTemplates = useMemo(
    () =>
      tagFilter === "all"
        ? TEMPLATES
        : TEMPLATES.filter((t) => t.tags.includes(tagFilter)),
    [tagFilter]
  );

  const activeImage = useMemo(
    () => images.find((i) => i.id === activeImageId) ?? null,
    [images, activeImageId]
  );

  useEffect(() => {
    setLogoReady(false);
    const img = new Image();
    img.src = logoSrc;
    img.onload = () => {
      logoRef.current = img;
      setLogoReady(true);
    };
  }, [logoSrc]);

  useEffect(() => {
    if (!activeImage) {
      bgImageRef.current = null;
      setBgReady((n) => n + 1);
      return;
    }
    const img = new Image();
    img.src = activeImage.src;
    img.onload = () => {
      bgImageRef.current = img;
      setBgReady((n) => n + 1);
    };
  }, [activeImage]);

  const onLogoFile = (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoSrc(String(reader.result));
      setCustomLogoName(file.name);
      toast.success("Logo updated");
    };
    reader.readAsDataURL(file);
  };

  const resetLogo = () => {
    setLogoSrc(chirpwellLogo);
    setCustomLogoName(null);
  };

  const onImageFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const item: UploadedImage = {
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          src: String(reader.result),
        };
        setImages((prev) => [...prev, item]);
        setActiveImageId(item.id);
        resetPan();
      };
      reader.readAsDataURL(file);
    });
    toast.success("Image added");
  };

  const resetPan = () => {
    setBgOffsetX(0);
    setBgOffsetY(0);
    setBgZoom(1);
  };

  const clampPan = (v: number) => Math.min(1, Math.max(-1, v));
  const clampZoom = (v: number) => Math.min(3, Math.max(1, v));

  const onCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!activeImage) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointersRef.current.values()];
    if (pts.length === 2) {
      // start pinch
      pinchRef.current = {
        dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        zoom: bgZoom,
      };
      dragRef.current = null;
    } else {
      dragRef.current = { x: e.clientX, y: e.clientY, ox: bgOffsetX, oy: bgOffsetY };
    }
  };

  const onCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointersRef.current.values()];
    const rect = e.currentTarget.getBoundingClientRect();

    if (pts.length >= 2 && pinchRef.current) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (pinchRef.current.dist > 0) {
        setBgZoom(clampZoom(pinchRef.current.zoom * (dist / pinchRef.current.dist)));
      }
      return;
    }

    const d = dragRef.current;
    if (!d) return;
    // drag right moves the photo right => offset decreases
    setBgOffsetX(clampPan(d.ox - ((e.clientX - d.x) / rect.width) * 2));
    setBgOffsetY(clampPan(d.oy - ((e.clientY - d.y) / rect.height) * 2));
  };

  const onCanvasPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointersRef.current.delete(e.pointerId);
    const pts = [...pointersRef.current.entries()];
    if (pts.length < 2) pinchRef.current = null;
    if (pts.length === 1) {
      // continue panning with the remaining finger
      dragRef.current = { x: pts[0][1].x, y: pts[0][1].y, ox: bgOffsetX, oy: bgOffsetY };
    } else {
      dragRef.current = null;
    }
  };

  const onCanvasWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    if (!activeImage) return;
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    setBgZoom((z) => clampZoom(z * Math.exp(-dy * 0.0015)));
  };


  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((i) => i.id !== id));
    if (activeImageId === id) setActiveImageId(null);
  };

  const selectTemplate = (id: TemplateDef["id"]) => {
    const def = TEMPLATES.find((t) => t.id === id)!;
    setTemplateId(id);
    setContent({ ...EMPTY_CONTENT, ...def.defaults } as TemplateContent);
  };

  useEffect(() => {
    if (canvasRef.current) {
      renderTemplate(canvasRef.current, {
        template: templateId,
        size,
        palette,
        content,
        logo: logoRef.current,
        fonts,
        fontScales,
        decor,
        decorOpacity,
        decorColor,
        decorScale,
        decorRotation,
        decorPlacement,
        bgImage: bgImageRef.current,
        bgImageFit: imageFit,
        bgOffsetX,
        bgOffsetY,
        bgZoom,
        bgScrim,
        autoContrast,
        textColor,
      });
    }
  }, [
    templateId,
    size,
    palette,
    content,
    logoReady,
    fonts,
    fontScales,
    decor,
    decorOpacity,
    decorColor,
    decorScale,
    decorRotation,
    decorPlacement,
    bgReady,
    imageFit,
    bgOffsetX,
    bgOffsetY,
    bgZoom,
    bgScrim,
    autoContrast,
    textColor,
  ]);

  const performDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${customLogoName ? "graphic" : "chirpwell"}-${templateId}-${size.id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("Graphic downloaded — unreviewed draft", {
      description:
        "Verify every stat against its source and route through carrier/FMO advertising approval before publishing.",
    });
  };

  const download = () => {
    if (ackDone) {
      performDownload();
      return;
    }
    setAckChecked(false);
    setAckOpen(true);
  };


  const update = (key: keyof TemplateContent, value: string) =>
    setContent((c) => ({ ...c, [key]: value }));

  const updateBullet = (i: number, value: string) =>
    setContent((c) => ({
      ...c,
      bullets: c.bullets.map((b, idx) => (idx === i ? value : b)),
    }));

  /* ---------- panels ---------- */

  const sourcePanel = template.fields.includes("source") && (
    <div className={sectionClass} style={sectionStyle}>
      <div className="flex items-center justify-between gap-2">
        <p className={labelClass}>INDUSTRY REFERENCE</p>
        <span className="text-[10px] text-brand-muted">Daily · {todayKey()}</span>
      </div>
      <div className="space-y-2">
        {dailySources.map((s) => {
          const label = `${s.citation} — ${new URL(s.url).hostname.replace("www.", "")}`;
          const active = content.source === label;
          return (
            <div
              key={s.id}
              className="rounded-lg border transition-colors"
              style={{
                borderColor: active
                  ? "hsl(var(--brand-gold))"
                  : "hsl(var(--brand-line))",
                background: active ? "hsl(var(--brand-gold-soft))" : "transparent",
              }}
            >
              <button
                onClick={() =>
                  setContent((c) => {
                    if (active) return { ...c, source: "", sourceUrl: "" };
                    const has = (f: string) => template.fields.includes(f as never);
                    return {
                      ...c,
                      source: label,
                      sourceUrl: s.url,
                      // cleared so you can type the figure from the source
                      stat: has("stat") ? "" : c.stat,
                      eyebrow: has("eyebrow") ? s.topic : c.eyebrow,
                      headline: has("headline") ? s.headline : c.headline,
                      body: has("body") ? s.insight : c.body,
                      statLabel: has("statLabel") ? s.insight : c.statLabel,
                      footer: has("footer") ? s.outlet : c.footer,
                    };
                  })
                }
                className="w-full text-left px-3 pt-2 pb-1"
              >
                <p
                  className="text-xs font-semibold text-brand-ink"
                >
                  {s.topic}
                </p>
                <p className="text-[11px] text-brand-muted leading-snug mt-0.5">
                  {s.insight}
                </p>
              </button>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 pb-2 text-[10px] text-brand-sage/70 hover:text-brand-sage underline underline-offset-2 break-all"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                {s.outlet} — {new URL(s.url).hostname.replace("www.", "")}
              </a>
              {active && template.fields.includes("note") && (
                <div className="px-3 pb-3">
                  <span className="text-[10px] text-brand-muted">
                    Your take on this — appears under the headline
                  </span>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={content.note}
                    onChange={(e) => update("note", e.target.value)}
                    placeholder={s.notePrompt}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <input
        className={inputClass}
        value={content.source}
        onChange={(e) => update("source", e.target.value)}
        placeholder="Or type your own source"
      />
    </div>
  );

  const contentPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>CONTENT</p>
      {template.fields.includes("eyebrow") && (
        <label className="block">
          <span className="text-xs text-brand-muted">Top label — category or topic</span>
          <input
            className={inputClass}
            value={content.eyebrow}
            onChange={(e) => update("eyebrow", e.target.value)}
            placeholder="Small label"
          />
        </label>
      )}
      {template.fields.includes("stat") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            Headline stat — the number people remember
          </span>
          <input
            className={inputClass}
            value={content.stat}
            onChange={(e) => update("stat", e.target.value)}
            placeholder="60%"
          />
        </label>
      )}
      {template.fields.includes("statLabel") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            What the stat means — one short sentence
          </span>
          <textarea
            rows={2}
            className={inputClass}
            value={content.statLabel}
            onChange={(e) => update("statLabel", e.target.value)}
            placeholder="of ancillary revenue brokers leave on the table"
          />
        </label>
      )}
      {template.fields.includes("headline") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            Main message — the biggest text on the graphic
          </span>
          <textarea
            rows={3}
            className={inputClass}
            value={content.headline}
            onChange={(e) => update("headline", e.target.value)}
          />
        </label>
      )}
      {template.fields.includes("body") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            Supporting detail — context under the main message
          </span>
          <textarea
            rows={3}
            className={inputClass}
            value={content.body}
            onChange={(e) => update("body", e.target.value)}
          />
        </label>
      )}
      {template.fields.includes("bullets") && (
        <div className="space-y-2">
          <span className="text-xs text-brand-muted">List items</span>
          {content.bullets.map((b, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={inputClass}
                value={b}
                onChange={(e) => updateBullet(i, e.target.value)}
              />
              <button
                onClick={() =>
                  setContent((c) => ({
                    ...c,
                    bullets: c.bullets.filter((_, idx) => idx !== i),
                  }))
                }
                className="px-2 rounded-lg border border-brand-line text-brand-muted hover:text-red-400"
                aria-label="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {content.bullets.length < 5 && (
            <button
              onClick={() => setContent((c) => ({ ...c, bullets: [...c.bullets, ""] }))}
              className="flex items-center gap-2 text-xs text-brand-sage hover:text-brand-sage"
            >
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          )}
        </div>
      )}
      {template.fields.includes("note") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            Your note — why your business is winning / helping
          </span>
          <textarea
            rows={3}
            className={inputClass}
            value={content.note}
            onChange={(e) => update("note", e.target.value)}
            placeholder="What you do differently for brokers."
          />
        </label>
      )}
      {template.fields.includes("footer") && (
        <label className="block">
          <span className="text-xs text-brand-muted">
            {templateId === "cta" ? "Button text" : "Footer"}
          </span>
          <input
            className={inputClass}
            value={content.footer}
            onChange={(e) => update("footer", e.target.value)}
          />
        </label>
      )}
    </div>
  );

  const imagePanel = (
    <div className={sectionClass} style={sectionStyle}>
      <div className="flex items-center justify-between">
        <p className={labelClass}>IMAGES</p>
        <button
          onClick={() => imageInputRef.current?.click()}
          className="flex items-center gap-1 text-[11px] text-brand-sage hover:text-brand-sage"
        >
          <Upload className="w-3 h-3" /> Upload
        </button>
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          onImageFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {images.length === 0 ? (
        <p className="text-[11px] text-brand-muted leading-snug">
          Upload photos to use as template backgrounds. Text colors adjust
          automatically for contrast.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setActiveImageId(null)}
              className={`aspect-square rounded-lg border text-[10px] text-brand-muted flex items-center justify-center ${
                activeImageId === null
                  ? "border-brand-gold bg-brand-gold-soft"
                  : "border-brand-line hover:border-brand-line"
              }`}
            >
              No image
            </button>
            {images.map((img) => (
              <div key={img.id} className="relative">
                <button
                  onClick={() => {
                    setActiveImageId(img.id);
                    resetPan();
                  }}
                  className={`block w-full aspect-square rounded-lg overflow-hidden border ${
                    activeImageId === img.id
                      ? "border-brand-gold"
                      : "border-brand-line hover:border-brand-line"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.name}
                    className="h-full w-full object-cover"
                  />
                </button>
                <button
                  onClick={() => removeImage(img.id)}
                  className="absolute -top-1.5 -right-1.5 rounded-full bg-brand-surface border border-brand-line p-0.5 text-brand-muted hover:text-red-400"
                  aria-label={`Remove ${img.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>

          {activeImage && (
            <div className="space-y-3 rounded-lg border border-brand-line bg-brand-paper p-3">
              <div className="grid grid-cols-2 gap-2">
                {IMAGE_FITS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setImageFit(f.id);
                      setBgOffsetY(0);
                    }}
                    className={`rounded-md border px-2 py-1.5 text-[11px] transition-colors ${
                      imageFit === f.id
                        ? "border-brand-gold bg-brand-gold-soft text-brand-ink"
                        : "border-brand-line text-brand-muted hover:border-brand-line"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
                  Tint
                </span>
                <input
                  type="range"
                  min={0}
                  max={0.9}
                  step={0.05}
                  value={bgScrim}
                  onChange={(e) => setBgScrim(Number(e.target.value))}
                  className="flex-1 accent-brand-ink"
                />
                <span className="text-[11px] text-brand-muted w-11 text-right tabular-nums">
                  {Math.round(bgScrim * 100)}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
                  Zoom
                </span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={bgZoom}
                  onChange={(e) => setBgZoom(Number(e.target.value))}
                  className="flex-1 accent-brand-ink"
                />
                <span className="text-[11px] text-brand-muted w-11 text-right tabular-nums">
                  {bgZoom.toFixed(2)}x
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
                  Pan X
                </span>
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.02}
                  value={bgOffsetX}
                  onChange={(e) => setBgOffsetX(Number(e.target.value))}
                  className="flex-1 accent-brand-ink"
                />
                <span className="text-[11px] text-brand-muted w-11 text-right tabular-nums">
                  {Math.round(bgOffsetX * 100)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
                  Pan Y
                </span>
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.02}
                  value={bgOffsetY}
                  onChange={(e) => setBgOffsetY(Number(e.target.value))}
                  className="flex-1 accent-brand-ink"
                />
                <span className="text-[11px] text-brand-muted w-11 text-right tabular-nums">
                  {Math.round(bgOffsetY * 100)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] text-brand-muted leading-snug">
                  Drag the preview to reposition. Pinch (or scroll) on the preview to zoom.
                </p>
                <button
                  onClick={resetPan}
                  className="rounded-md border border-brand-line px-2 py-1 text-[10px] text-brand-muted hover:border-brand-line whitespace-nowrap"
                >
                  Reset crop
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const textColorPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>TEXT COLOR</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => {
            setAutoContrast(true);
            setTextColor("");
          }}
          className={`rounded-md border px-2 py-2 text-[11px] transition-colors ${
            autoContrast && !textColor
              ? "border-brand-gold bg-brand-gold-soft text-brand-ink"
              : "border-brand-line text-brand-muted hover:border-brand-line"
          }`}
        >
          Auto contrast
        </button>
        <button
          onClick={() => {
            setAutoContrast(false);
            if (!textColor) setTextColor(palette.text);
          }}
          className={`rounded-md border px-2 py-2 text-[11px] transition-colors ${
            !autoContrast || textColor
              ? "border-brand-gold bg-brand-gold-soft text-brand-ink"
              : "border-brand-line text-brand-muted hover:border-brand-line"
          }`}
        >
          Manual
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={isValidHex(textColor) ? textColor : palette.text}
          onChange={(e) => {
            setTextColor(e.target.value);
            setAutoContrast(false);
          }}
          className="h-8 w-10 rounded border border-brand-line bg-transparent"
          aria-label="Text color"
        />
        <input
          value={textColor}
          placeholder={autoContrast ? "Auto" : palette.text}
          onChange={(e) => setTextColor(e.target.value)}
          className="flex-1 rounded-md bg-brand-surface border border-brand-line px-2 py-1.5 text-xs text-brand-ink placeholder:text-brand-muted outline-none"
          spellCheck={false}
        />
      </div>
      <p className="text-[10px] text-brand-muted leading-snug">
        Auto contrast reads the background (and any uploaded photo) and picks text
        and accent tones that stay legible.
      </p>
    </div>
  );

  const typographyPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>TYPOGRAPHY</p>
      {(Object.keys(FONT_SLOT_LABELS) as FontSlot[])
        .filter((slot) => template.fields.includes(slot as never))
        .map((slot) => {
          const scale = fontScales[slot] ?? 1;
          return (
            <div
              key={slot}
              className="rounded-lg border border-brand-line bg-brand-paper p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-brand-muted">{FONT_SLOT_LABELS[slot]}</span>
                <select
                  value={fonts[slot] ?? DEFAULT_FONTS[slot]}
                  onChange={(e) =>
                    setFonts((f) => ({ ...f, [slot]: e.target.value as FontId }))
                  }
                  className="rounded-md bg-brand-surface border border-brand-line text-xs text-brand-ink px-2 py-1.5 outline-none"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id} className="bg-brand-surface">
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-brand-muted w-8">
                  Size
                </span>
                <input
                  type="range"
                  min={FONT_SCALE_MIN}
                  max={FONT_SCALE_MAX}
                  step={0.05}
                  value={scale}
                  onChange={(e) =>
                    setFontScales((f) => ({ ...f, [slot]: Number(e.target.value) }))
                  }
                  className="flex-1 accent-brand-ink"
                />
                <span className="text-[11px] text-brand-muted w-10 text-right tabular-nums">
                  {Math.round(scale * 100)}%
                </span>
              </div>
            </div>
          );
        })}
      <button
        onClick={() => {
          setFonts({});
          setFontScales({});
        }}
        className="text-[11px] text-brand-muted hover:text-brand-muted"
      >
        Reset fonts & sizes
      </button>
    </div>
  );

  const decorPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>SHAPES & AD DESIGN</p>
      <div className="grid grid-cols-2 gap-2">
        {DECOR_OPTIONS.map((d) => (
          <button
            key={d.id}
            onClick={() => setDecor(d.id)}
            className={`rounded-lg border px-3 py-2 text-left transition-colors ${
              decor === d.id
                ? "border-brand-gold bg-brand-gold-soft"
                : "border-brand-line hover:border-brand-line"
            }`}
          >
            <span className="block text-xs text-brand-ink">{d.label}</span>
            <span className="block text-[10px] text-brand-muted">{d.hint}</span>
          </button>
        ))}
      </div>
      {decor !== "none" && (
        <div className="space-y-3 rounded-lg border border-brand-line bg-brand-paper p-3">
          {[
            {
              label: "Strength",
              value: decorOpacity,
              min: 0.03,
              max: 0.85,
              step: 0.01,
              set: setDecorOpacity,
              fmt: (v: number) => `${Math.round(v * 100)}%`,
            },
            {
              label: "Size",
              value: decorScale,
              min: 0.4,
              max: 2,
              step: 0.05,
              set: setDecorScale,
              fmt: (v: number) => `${Math.round(v * 100)}%`,
            },
            {
              label: "Angle",
              value: decorRotation,
              min: 0,
              max: 360,
              step: 5,
              set: setDecorRotation,
              fmt: (v: number) => `${Math.round(v)}\u00B0`,
            },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
                {c.label}
              </span>
              <input
                type="range"
                min={c.min}
                max={c.max}
                step={c.step}
                value={c.value}
                onChange={(e) => c.set(Number(e.target.value))}
                className="flex-1 accent-brand-ink"
              />
              <span className="text-[11px] text-brand-muted w-11 text-right tabular-nums">
                {c.fmt(c.value)}
              </span>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-brand-muted w-14">
              Color
            </span>
            <input
              type="color"
              value={isValidHex(decorColor) ? decorColor : palette.accent}
              onChange={(e) => setDecorColor(e.target.value)}
              className="h-8 w-10 rounded border border-brand-line bg-transparent"
            />
            <input
              value={decorColor}
              placeholder="Palette accent"
              onChange={(e) => setDecorColor(e.target.value)}
              className="flex-1 rounded-md bg-brand-surface border border-brand-line px-2 py-1.5 text-xs text-brand-ink placeholder:text-brand-muted outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {DECOR_PLACEMENTS.map((pl) => (
              <button
                key={pl.id}
                onClick={() => setDecorPlacement(pl.id)}
                className={`rounded-md border px-2 py-1.5 text-[11px] transition-colors ${
                  decorPlacement === pl.id
                    ? "border-brand-gold bg-brand-gold-soft text-brand-ink"
                    : "border-brand-line text-brand-muted hover:border-brand-line"
                }`}
              >
                {pl.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              setDecorOpacity(0.18);
              setDecorScale(1);
              setDecorRotation(0);
              setDecorColor("");
              setDecorPlacement("default");
            }}
            className="text-[11px] text-brand-muted hover:text-brand-muted"
          >
            Reset shape design
          </button>
        </div>
      )}
    </div>
  );

  const formatPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>FORMAT</p>
      <div className="space-y-2">
        {SOCIAL_SIZES.map((s) => (
          <button
            key={s.id}
            onClick={() => setSizeId(s.id)}
            className="w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors"
            style={{
              background: s.id === sizeId ? "hsl(var(--brand-gold-soft))" : "transparent",
              borderColor:
                s.id === sizeId ? "hsl(var(--brand-gold))" : "hsl(var(--brand-line))",
              color: s.id === sizeId ? "hsl(var(--brand-ink))" : "hsl(var(--brand-muted))",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );

  const palettePanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>PALETTE</p>
      <div className="grid grid-cols-2 gap-2">
        {PALETTES.map((p) => (
          <button
            key={p.id}
            onClick={() => setPaletteId(p.id)}
            className="rounded-lg p-3 border text-left transition-colors"
            style={{
              borderColor:
                p.id === paletteId ? "hsl(var(--brand-gold))" : "hsl(var(--brand-line))",
              background: "hsl(var(--brand-surface))",
            }}
          >
            <div className="flex gap-1 mb-2">
              <span className="w-4 h-4 rounded-full" style={{ background: p.bg }} />
              <span className="w-4 h-4 rounded-full" style={{ background: p.accent }} />
              <span className="w-4 h-4 rounded-full" style={{ background: p.bgAlt }} />
            </div>
            <span className="text-xs text-brand-muted">{p.label}</span>
          </button>
        ))}
      </div>

      <button
        onClick={() => setPaletteId("custom")}
        className="w-full rounded-lg p-3 border text-left transition-colors"
        style={{
          borderColor:
            paletteId === "custom" ? "hsl(var(--brand-gold))" : "hsl(var(--brand-line))",
          background: "hsl(var(--brand-surface))",
        }}
      >
        <div className="flex gap-1 mb-2">
          {[custom.bg, custom.accent, custom.bgAlt].map((c, i) => (
            <span
              key={i}
              className="w-4 h-4 rounded-full border border-brand-line"
              style={{ background: isValidHex(c) ? c : "transparent" }}
            />
          ))}
        </div>
        <span className="text-xs text-brand-muted">Custom hex palette</span>
      </button>

      {paletteId === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ["bg", "Background"],
              ["bgAlt", "Gradient end"],
              ["accent", "Accent"],
              ["text", "Text"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-[11px] text-brand-muted">{label}</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={isValidHex(custom[key]) ? custom[key] : "#000000"}
                  onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))}
                  className="w-8 h-8 rounded bg-transparent border border-brand-line cursor-pointer"
                  aria-label={`${label} color`}
                />
                <input
                  className={inputClass}
                  value={custom[key]}
                  onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))}
                  placeholder="#000000"
                  spellCheck={false}
                />
              </div>
              {!isValidHex(custom[key]) && (
                <span className="text-[10px] text-red-400">
                  Enter a hex like #1A2B3C
                </span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );

  const logoPanel = (
    <div className={sectionClass} style={sectionStyle}>
      <p className={labelClass}>LOGO</p>
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-brand-surface border border-brand-line flex items-center justify-center overflow-hidden">
          <img src={logoSrc} alt="Logo preview" className="max-h-9 max-w-10 object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-brand-ink truncate">
            {customLogoName ?? "Chirpwell logo"}
          </p>
          <div className="flex gap-3 mt-1">
            <button
              onClick={() => logoInputRef.current?.click()}
              className="flex items-center gap-1 text-[11px] text-brand-sage hover:text-brand-sage"
            >
              <Upload className="w-3 h-3" /> Upload your logo
            </button>
            {customLogoName && (
              <button
                onClick={resetLogo}
                className="flex items-center gap-1 text-[11px] text-brand-muted hover:text-brand-muted"
              >
                <ImageIcon className="w-3 h-3" /> Use Chirpwell
              </button>
            )}
          </div>
        </div>
      </div>
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onLogoFile(e.target.files?.[0])}
      />
    </div>
  );

  return (
    <div className="brand-site min-h-screen">
      <Seo
        title="Social Media Graphic Templates for Insurance Brokers | Chirpwell"
        description="Build branded social graphics for health insurance marketing and producer recruiting. Editable templates, industry-sourced stats, and instant PNG export."
        path="/social-templates"
      />
      <Header />
      <div className="container pt-44 sm:pt-36 pb-16 sm:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-8"
        >
          <p className="text-xs font-semibold tracking-[0.25em] text-brand-sage mb-3">
            SOCIAL MEDIA MARKETING
          </p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-brand-ink mb-3">
            Graphic Templates
          </h1>
          <p className="text-brand-muted">
            One gallery, every template. Filter by what the post needs to do —
            attract clients or recruit producers — then edit the copy, drop in your
            own images and download a ready-to-post PNG.
          </p>
        </motion.div>

        {/* Intent filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {TAG_FILTERS.map((f) => {
            const active = f.id === tagFilter;
            return (
              <button
                key={f.id}
                onClick={() => setTagFilter(f.id)}
                className="rounded-full px-4 py-1.5 border text-xs font-semibold transition-colors"
                style={{
                  background: active ? "hsl(var(--brand-gold-soft))" : "hsl(var(--brand-surface))",
                  borderColor: active ? "hsl(var(--brand-gold))" : "hsl(var(--brand-line))",
                  color: active ? "hsl(var(--brand-ink))" : "hsl(var(--brand-muted))",
                }}
              >
                {f.label}
              </button>
            );
          })}
          <span className="text-xs text-brand-muted ml-1">
            {visibleTemplates.length} template
            {visibleTemplates.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Template picker */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6 sm:mb-8">
          {visibleTemplates.map((t) => {
            const active = t.id === templateId;
            return (
              <button
                key={t.id}
                onClick={() => selectTemplate(t.id)}
                className="text-left rounded-xl p-3 sm:p-4 transition-all duration-200 border flex flex-col"
                style={{
                  background: active ? "hsl(var(--brand-gold-soft))" : "hsl(var(--brand-surface))",
                  borderColor: active ? "hsl(var(--brand-gold))" : "hsl(var(--brand-surface))",
                }}
              >
                <p
                  className="font-semibold text-sm mb-1 text-brand-ink"
                >
                  {t.name}
                </p>
                <p className="text-xs text-brand-muted leading-snug">{t.description}</p>
                <div className="flex flex-wrap gap-1 mt-2.5">
                  {t.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide"
                      style={{
                        background: "hsl(var(--brand-gold-soft))",
                        color: "hsl(var(--brand-ink))",
                      }}
                    >
                      {TAG_LABEL[tag]}
                    </span>
                  ))}
                </div>
              </button>
            );

          })}
        </div>

        {/* Editor: controls | canvas | controls */}
        <div className="grid gap-4 sm:gap-6 xl:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,340px)] items-start">
          {/* Left rail */}
          <div className="space-y-4 sm:space-y-6 order-2 xl:order-1">
            {sourcePanel}
            {contentPanel}
          </div>

          {/* Canvas — center stage */}
          <div className="order-1 xl:order-2 xl:sticky xl:top-28">
            <div
              className="rounded-2xl p-3 sm:p-6 border flex flex-col items-center"
              style={{
                background: "hsl(var(--brand-surface))",
                borderColor: "hsl(var(--brand-surface))",
              }}
            >
              <canvas
                ref={canvasRef}
                onPointerDown={onCanvasPointerDown}
                onPointerMove={onCanvasPointerMove}
                onPointerUp={onCanvasPointerUp}
                onPointerCancel={onCanvasPointerUp}
                onWheel={onCanvasWheel}
                className="rounded-xl shadow-2xl max-w-full max-h-[52vh] sm:max-h-[68vh] select-none"
                style={{
                  cursor: activeImage ? "grab" : "default",
                  touchAction: activeImage ? "none" : undefined,
                  width: size.width > size.height ? "100%" : "auto",
                  aspectRatio: `${size.width} / ${size.height}`,
                }}
              />
              <p className="mt-4 text-[11px] sm:text-xs text-brand-muted text-center">
                {size.width} × {size.height} px · exports at full resolution
              </p>
              {activeImage && (
                <p className="mt-1 text-[11px] text-brand-muted text-center">
                  Drag to pan · pinch or scroll to zoom
                </p>
              )}

              {content.sourceUrl && (
                <a
                  href={content.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-1 text-xs text-brand-sage/80 hover:text-brand-sage underline underline-offset-2 break-all text-center"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  Open source: {content.source}
                </a>
              )}
              <div
                className="mt-5 w-full rounded-xl p-4 text-left"
                style={{
                  background: "hsl(var(--brand-gold-soft))",
                  border: "1px solid hsl(var(--brand-gold))",
                }}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-brand-sage" />
                  <div className="text-[11px] sm:text-xs leading-relaxed text-brand-ink">
                    <p className="font-semibold text-brand-sage mb-1">
                      Before you post: generic, unreviewed draft
                    </p>
                    <p>
                      All template copy and any numbers shown are <strong>generic placeholders</strong> and may not
                      reflect actual, current data. Replace them with a figure from a cited industry reference or your
                      own verifiable book data before publishing. You are the responsible party for anything you
                      publish. Medicare Advantage / Part D marketing is subject to CMS marketing rules (42 CFR 422
                      &amp; 423, MCMG) and may require carrier and/or CMS approval. All materials are subject to your
                      state DOI advertising rules. Route the piece through your carrier/FMO advertising-approval
                      process first. Chirpwell does not review or approve materials and provides no compliance advice.
                    </p>

                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 w-full">
                <button
                  onClick={download}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold text-sm bg-brand-ink text-brand-inverse hover:bg-brand-ink/90"
                >
                  <Download className="w-4 h-4" /> Download PNG
                </button>
                <button
                  onClick={() => selectTemplate(templateId)}
                  className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm border border-brand-line text-brand-muted hover:text-brand-ink"
                >
                  <RotateCcw className="w-4 h-4" /> Reset
                </button>
              </div>

              <AlertDialog open={ackOpen} onOpenChange={setAckOpen}>
                <AlertDialogContent className="bg-brand-surface border-brand-gold text-brand-ink">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-brand-sage">
                      <AlertTriangle className="h-5 w-5" /> Marketing compliance check
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-brand-muted text-sm leading-relaxed">
                      This graphic is a generic, unreviewed draft — the copy and any figures are placeholders that may
                      not reflect actual data. Use a cited industry reference or your own verifiable data. Medicare
                      Advantage / Part D marketing is subject to CMS marketing rules (42 CFR 422 &amp; 423, MCMG) and
                      may require carrier and/or CMS approval, and all materials are subject to your state DOI
                      advertising rules. Chirpwell does not review, approve, or file your materials and provides no
                      legal or compliance advice.

                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <label className="flex items-start gap-3 rounded-lg p-3 cursor-pointer bg-brand-gold-soft border border-brand-gold">
                    <Checkbox
                      checked={ackChecked}
                      onCheckedChange={(v) => setAckChecked(v === true)}
                      className="mt-0.5 border-brand-gold data-[state=checked]:bg-brand-ink data-[state=checked]:text-brand-ink"
                    />
                    <span className="text-xs leading-relaxed text-brand-muted">
                      I'm the responsible party for this piece and will verify the figures and obtain carrier/FMO (and any
                      required CMS) approval before publishing.
                    </span>
                  </label>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-brand-line text-brand-muted hover:text-brand-ink">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={!ackChecked}
                      onClick={() => {
                        setAckDone(true);
                        try {
                          sessionStorage.setItem("cw-marketing-ack", "1");
                        } catch {
                          /* ignore */
                        }
                        performDownload();
                      }}
                      className="bg-brand-ink text-brand-inverse hover:bg-brand-ink/90 disabled:opacity-40"
                    >
                      <Download className="w-4 h-4 mr-2" /> I understand — download
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>



            </div>
          </div>

          {/* Right rail */}
          <div className="space-y-4 sm:space-y-6 order-3">
            {formatPanel}
            {imagePanel}
            {palettePanel}
            {textColorPanel}
            {typographyPanel}
            {decorPanel}
            {logoPanel}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SocialTemplates;
